/**
 * Undo/Redo Tracking System
 * Tracks the last operation (metric creation, enablement, or value updates)
 * to allow users to undo/redo within 5 minutes
 */

export type UndoOperation = {
  timestamp: number;
  type: 'metric_value_update' | 'custom_metric_created' | 'builtin_metric_enabled';
  userId: string;
  entryDate: string; // Format: yyyy-MM-dd
  
  // For metric value updates
  metricUpdates?: {
    metricKey: string;
    previousValue: number | null;
    newValue: number;
  }[];
  
  // For custom metric creation
  customMetricCreated?: {
    metricKey: string;
    metricId: string;
  };
  
  // For built-in metric enablement
  builtinMetricEnabled?: {
    metricKey: string;
    category: 'nutrition_metrics' | 'activity_sleep_metrics' | 'results_metrics';
  };
};

const UNDO_STORAGE_KEY = 'gitfit_last_operation';
const REDO_STORAGE_KEY = 'gitfit_last_undone_operation';
const UNDO_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Save an operation to localStorage for undo tracking
 */
export function trackOperation(operation: Omit<UndoOperation, 'timestamp'>): void {
  const op: UndoOperation = {
    ...operation,
    timestamp: Date.now(),
  };
  localStorage.setItem(UNDO_STORAGE_KEY, JSON.stringify(op));
  
  // Dispatch event so UI can update
  window.dispatchEvent(new CustomEvent('undo-operation-tracked'));
}

/**
 * Get the last tracked operation if it's within the undo window
 */
export function getLastOperation(): UndoOperation | null {
  const stored = localStorage.getItem(UNDO_STORAGE_KEY);
  if (!stored) return null;
  
  try {
    const operation: UndoOperation = JSON.parse(stored);
    const age = Date.now() - operation.timestamp;
    
    // Check if operation is still within undo window
    if (age > UNDO_TIMEOUT_MS) {
      clearLastOperation();
      return null;
    }
    
    return operation;
  } catch (error) {
    console.error('[UndoTracking] Failed to parse stored operation:', error);
    clearLastOperation();
    return null;
  }
}

/**
 * Clear the last tracked operation
 */
export function clearLastOperation(): void {
  localStorage.removeItem(UNDO_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('undo-operation-cleared'));
}

/**
 * Save an undone operation to localStorage for redo tracking
 * This is called when an undo is performed, storing the inverse operation
 */
export function trackRedoOperation(operation: Omit<UndoOperation, 'timestamp'>): void {
  const op: UndoOperation = {
    ...operation,
    timestamp: Date.now(),
  };
  localStorage.setItem(REDO_STORAGE_KEY, JSON.stringify(op));
  
  // Dispatch event so UI can update
  window.dispatchEvent(new CustomEvent('redo-operation-tracked'));
}

/**
 * Get the last undone operation if it's within the redo window
 */
export function getLastUndoneOperation(): UndoOperation | null {
  const stored = localStorage.getItem(REDO_STORAGE_KEY);
  if (!stored) return null;
  
  try {
    const operation: UndoOperation = JSON.parse(stored);
    const age = Date.now() - operation.timestamp;
    
    // Check if operation is still within redo window
    if (age > UNDO_TIMEOUT_MS) {
      clearLastUndoneOperation();
      return null;
    }
    
    return operation;
  } catch (error) {
    console.error('[UndoTracking] Failed to parse stored redo operation:', error);
    clearLastUndoneOperation();
    return null;
  }
}

/**
 * Clear the last undone operation (redo)
 */
export function clearLastUndoneOperation(): void {
  localStorage.removeItem(REDO_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('redo-operation-cleared'));
}

/**
 * Check if there's a redoable operation available
 */
export function hasRedoableOperation(): boolean {
  return getLastUndoneOperation() !== null;
}

/**
 * Get the time remaining for redo (in milliseconds)
 */
export function getRedoTimeRemaining(): number | null {
  const operation = getLastUndoneOperation();
  if (!operation) return null;
  
  const age = Date.now() - operation.timestamp;
  const remaining = UNDO_TIMEOUT_MS - age;
  
  return remaining > 0 ? remaining : null;
}

/**
 * Check if there's an undoable operation available
 */
export function hasUndoableOperation(): boolean {
  return getLastOperation() !== null;
}

/**
 * Get the time remaining for undo (in milliseconds)
 */
export function getUndoTimeRemaining(): number | null {
  const operation = getLastOperation();
  if (!operation) return null;
  
  const age = Date.now() - operation.timestamp;
  const remaining = UNDO_TIMEOUT_MS - age;
  
  return remaining > 0 ? remaining : null;
}
